/**
 * The live room's match video, in sync mode: the protocol on the room's data
 * channel and the follower's arithmetic. Pure, so it is tested on its own
 * (roomMatchSync.test.ts).
 *
 * The coach's player is the source. Anyone moves the video, and there is no
 * driver and no lock (decision 100): the player's moves go to the coach as
 * 'ctl', which the coach applies. The coach sends play, pause, seek and rate
 * as they happen, with who moved it, a state heartbeat at 10 Hz per panel,
 * and its drawings as they are drawn. The player follows, by seeking only:
 * paused, it seeks to the coach's frame; playing, it estimates where the
 * coach is now (the message's time plus half the measured round trip, at
 * the coach's rate) and seeks when it is more than SEEK_DRIFT away. It never
 * changes its rate to catch up: a nudged rate is the video running while
 * the other side scrubs it back, which read as playing in reverse.
 * A scrub travels as its start, each point and its end ('scrub' with a
 * phase), and the other side runs it through its own player's scrub
 * (LessonPreview's ref): the async lesson player's behaviour, one code path.
 * Whether it pauses, and whether it plays on after, is that player's rule.
 */

export const MATCH_SYNC_VERSION = 1;
/** Heartbeat interval: 10 Hz. */
export const HEARTBEAT_MS = 100;
/** Paused, a follower further than this from the coach's frame seeks to it. */
export const PAUSED_TOLERANCE = 0.03;
/** Playing, a drift beyond this is a seek; inside it the video is left alone. */
export const SEEK_DRIFT = 0.25;

export type Panel = 0 | 1;
export type MatchTool = 'brush' | 'arrow' | 'circle';

/** A drawing in the video's own content box, 0 to 1 on each axis, so it lands on the same pixels at any size. */
export interface MatchStroke {
  id: string;
  panel: Panel;
  tool: MatchTool;
  /** A colour name from DRAW_COLORS. */
  color: string;
  /** Brush: the path. Arrow: tail and head. Circle: two opposite corners of its box. */
  points: [number, number][];
}

export interface PanelSource {
  url: string;
  label: string;
}

/** Who moved the video, for "Ana moved to 4:12" (decision 74). */
export type Driver = 'coach' | 'player';

/**
 * The player's own move, sent to the coach's player, which stays the one
 * source: it applies the move and its heartbeat carries it back to both.
 * A scrub is its start, its points and its end, run by the other side's
 * player as its own scrub.
 */
export type ScrubPhase = 'start' | 'move' | 'end';
export type MatchControl =
  | { action: 'seek'; time: number }
  | { action: 'scrub'; phase: ScrubPhase; time: number; scrubbing?: boolean }
  | { action: 'play' }
  | { action: 'pause' }
  | { action: 'rate'; rate: number };

export type MatchMessage =
  | { t: 'mv'; v: number; kind: 'open'; panels: PanelSource[]; strokes: MatchStroke[]; states: PanelState[] }
  | { t: 'mv'; v: number; kind: 'close' }
  | { t: 'mv'; v: number; kind: 'state'; state: PanelState }
  | { t: 'mv'; v: number; kind: 'seek'; panel: Panel; time: number; by?: Driver }
  | { t: 'mv'; v: number; kind: 'scrub'; panel: Panel; phase: ScrubPhase; time: number; by?: Driver; scrubbing?: boolean }
  | { t: 'mv'; v: number; kind: 'ctl'; panel: Panel; control: MatchControl }
  | ({ t: 'mv'; v: number; kind: 'moved' } & MovedEvent)
  | { t: 'mv'; v: number; kind: 'stroke'; stroke: MatchStroke }
  | { t: 'mv'; v: number; kind: 'unstroke'; id: string }
  | { t: 'mv'; v: number; kind: 'clear' }
  | { t: 'mv'; v: number; kind: 'hello' }
  | { t: 'mv'; v: number; kind: 'ping'; id: number }
  | { t: 'mv'; v: number; kind: 'pong'; id: number };

/**
 * The driver event stream (docs/room-data-channel.md, 2026-10-28): one per
 * move, in the order the coach's player applied it, so both sides say
 * "Ana moved to 4:12" from the same record. Only the coach sends it.
 */
export interface MovedEvent {
  /** 1, 2, 3, … per open of the video; a gap means one was missed. */
  seq: number;
  panel: Panel;
  by: Driver;
  action: 'seek' | 'play' | 'pause' | 'rate';
  /** Where the video is after the move, seconds. */
  time: number;
  rate?: number;
  /** When the coach applied it, ISO. */
  at: string;
}

/** The stream's next event, numbered after `last` (0 before the first). */
export function movedEvent(last: number, e: Omit<MovedEvent, 'seq' | 'at'>, now: Date = new Date()): MatchMessage {
  return msg({ kind: 'moved', seq: last + 1, ...e, at: now.toISOString() });
}

/** "Ana moved to 4:12": the other side's move, in the follower's words. */
export function movedLine(e: Pick<MovedEvent, 'time'>, name: string): string {
  const s = Math.max(0, Math.floor(e.time));
  return `${name} moved to ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export interface PanelState {
  panel: Panel;
  time: number;
  playing: boolean;
  rate: number;
}

type Body = MatchMessage extends infer M ? (M extends { t: 'mv' } ? Omit<M, 't' | 'v'> : never) : never;

/** Wraps a body for the wire. */
export function msg(body: Body): MatchMessage {
  return { t: 'mv', v: MATCH_SYNC_VERSION, ...body } as MatchMessage;
}

/** Whether something off the data channel is one of ours, in a version this build reads. */
export function isMatchMessage(data: unknown): data is MatchMessage {
  return !!data && typeof data === 'object' && (data as { t?: unknown }).t === 'mv'
    && (data as { v?: unknown }).v === MATCH_SYNC_VERSION && typeof (data as { kind?: unknown }).kind === 'string';
}

export interface FollowAction {
  /** Seek here, when set. */
  seekTo?: number;
  /** Play or pause to match. */
  play?: boolean;
  /** The rate to run at. */
  rate: number;
}

/**
 * What the follower does with the coach's state.
 * `now` is the follower's own time, `latency` the one-way delay in seconds.
 */
export function followState(now: { time: number; playing: boolean }, coach: PanelState, latency: number): FollowAction {
  if (!coach.playing) {
    return {
      ...(Math.abs(now.time - coach.time) > PAUSED_TOLERANCE ? { seekTo: coach.time } : {}),
      ...(now.playing ? { play: false } : {}),
      rate: coach.rate,
    };
  }
  const target = coach.time + Math.max(0, latency) * coach.rate;
  const play = now.playing ? {} : { play: true };
  // Seek only, and always at the coach's own rate: never a nudged one.
  if (Math.abs(now.time - target) > SEEK_DRIFT) return { seekTo: target, ...play, rate: coach.rate };
  return { ...play, rate: coach.rate };
}

/**
 * A scrub on the wire (3 Nov, job 3): while a side drags, its points go at most every SCRUB_SEND_MS with
 * `scrubbing: true`, the latest one waiting for its slot rather than dropped, and the release goes at once
 * as the exact final position (`scrubbing: false`). The far side runs the points through its player's own
 * scrub (fastSeek where the browser has it) and the release as an exact seek; meanwhile it leaves the
 * heartbeat alone for that panel, so a drag backwards is never pulled forwards by a state from before it.
 */
export const SCRUB_SEND_MS = 100;
/** A remote scrub this long without a point is over, even if its release never came. */
export const SCRUB_QUIET_MS = 1500;

export function scrubSend(p: { phase: ScrubPhase; lastSentAt: number | null; now: number }): 'now' | 'later' {
  if (p.phase !== 'move') return 'now';
  return p.lastSentAt === null || p.now - p.lastSentAt >= SCRUB_SEND_MS ? 'now' : 'later';
}

/** The follower leaves a panel's heartbeat alone while the other side scrubs it. */
export function scrubHoldsState(lastPointAt: number | null, now: number): boolean {
  return lastPointAt !== null && now - lastPointAt < SCRUB_QUIET_MS;
}

/** One-way latency from a measured round trip, smoothed against the last estimate. */
export function nextLatency(previous: number | null, roundTripMs: number): number {
  const sample = Math.max(0, roundTripMs) / 2000;
  return previous === null ? sample : previous * 0.7 + sample * 0.3;
}

/** One frame at the source's rate (30 fps unless known): the frame step. */
export function frameStep(time: number, direction: 1 | -1, duration: number, fps = 30): number {
  const next = time + direction / fps;
  return Math.max(0, Math.min(Number.isFinite(duration) && duration > 0 ? duration : next, next));
}

/**
 * The box a contained video actually covers inside its element, so a
 * drawing's 0 to 1 coordinates map onto the same picture on both sides.
 */
export function contentBox(boxW: number, boxH: number, videoW: number, videoH: number): { x: number; y: number; w: number; h: number } {
  if (!videoW || !videoH || !boxW || !boxH) return { x: 0, y: 0, w: boxW, h: boxH };
  const scale = Math.min(boxW / videoW, boxH / videoH);
  const w = videoW * scale;
  const h = videoH * scale;
  return { x: (boxW - w) / 2, y: (boxH - h) / 2, w, h };
}

/** The distance from a point to a segment, in the same 0 to 1 units. */
function toSegment(p: [number, number], a: [number, number], b: [number, number]): number {
  const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

/**
 * Whether the eraser at `p` touches a stroke (decision 102): within `tol`
 * of the brush's path or the arrow's shaft, or of a circle's outline from
 * before the circle went. Points in the video's content box, 0 to 1.
 */
export function strokeHit(s: MatchStroke, p: [number, number], tol: number): boolean {
  const pts = s.points;
  if (!pts.length) return false;
  if (s.tool === 'circle' && pts.length >= 2) {
    const [[x1, y1], [x2, y2]] = [pts[0], pts[pts.length - 1]];
    const [cx, cy, rx, ry] = [(x1 + x2) / 2, (y1 + y2) / 2, Math.abs(x2 - x1) / 2, Math.abs(y2 - y1) / 2];
    if (!rx || !ry) return Math.hypot(p[0] - cx, p[1] - cy) <= tol;
    const r = Math.hypot((p[0] - cx) / rx, (p[1] - cy) / ry);
    return Math.abs(r - 1) * Math.min(rx, ry) <= tol;
  }
  if (pts.length === 1) return Math.hypot(p[0] - pts[0][0], p[1] - pts[0][1]) <= tol;
  const path = s.tool === 'arrow' ? [pts[0], pts[pts.length - 1]] : pts;
  for (let i = 1; i < path.length; i += 1) if (toSegment(p, path[i - 1], path[i]) <= tol) return true;
  return false;
}
